import React, { useState, useRef, useEffect } from "react";
import { Pencil } from "lucide-react";

interface WorkflowTitleProps {
  title: string;
  onTitleChange: (newTitle: string) => void;
  description: string;
  onDescriptionChange: (newDescription: string) => void;
  readOnly?: boolean;
}

export const WorkflowTitle: React.FC<WorkflowTitleProps> = ({
  title,
  onTitleChange,
  description,
  onDescriptionChange,
  readOnly = false,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);

  const titleSpanRef = useRef<HTMLSpanElement>(null);
  const descriptionSpanRef = useRef<HTMLSpanElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLInputElement>(null);

  const handleTitleBlur = () => setIsEditingTitle(false);
  const handleDescriptionBlur = () => setIsEditingDescription(false);

  // Auto-resize description input
  useEffect(() => {
    if (
      isEditingDescription &&
      descriptionInputRef.current &&
      descriptionSpanRef.current
    ) {
      descriptionInputRef.current.style.width =
        Math.min(descriptionSpanRef.current.offsetWidth + 12, 360) + "px";
    }
  }, [description, isEditingDescription]);

  return (
    <div className="flex flex-col leading-tight">
      {/* Title Section */}
      <div className="flex items-center gap-2">
        {isEditingTitle && !readOnly ? (
          <>
            <span
              ref={titleSpanRef}
              className="absolute invisible whitespace-pre text-[15px] font-semibold"
            >
              {title || " "}
            </span>
            <input
              ref={titleInputRef}
              type="text"
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyDown={(e) => e.key === "Enter" && handleTitleBlur()}
              className="text-[15px] font-semibold text-gray-800 border border-gray-300 rounded px-1 focus:outline-none focus:ring focus:ring-blue-300"
              autoFocus
            />
          </>
        ) : (
          <h1 className="text-[15px] font-semibold text-gray-800 truncate max-w-[360px]" title={title}>{title}</h1>
        )}
        {!readOnly && (
          <Pencil
            size={14}
            className="text-gray-500 cursor-pointer hover:text-gray-700"
            onClick={() => setIsEditingTitle(true)}
          />
        )}
      </div>

      {/* Description Section */}
      <div className="flex items-center gap-2">
        {isEditingDescription && !readOnly ? (
          <>
            <span
              ref={descriptionSpanRef}
              className="absolute invisible whitespace-pre text-xs"
            >
              {description || " "}
            </span>
            <input
              ref={descriptionInputRef}
              type="text"
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              onBlur={handleDescriptionBlur}
              onKeyDown={(e) => e.key === "Enter" && handleDescriptionBlur()}
              className="text-xs text-gray-500 border border-gray-300 rounded px-1 focus:outline-none focus:ring focus:ring-blue-300"
              autoFocus
            />
          </>
        ) : (
          <span className="text-xs text-gray-500 block truncate max-w-[360px]" title={description}>{description}</span>
        )}
        {!readOnly && (
          <Pencil
            size={12}
            className="text-gray-400 cursor-pointer hover:text-gray-600"
            onClick={() => setIsEditingDescription(true)}
          />
        )}
      </div>
    </div>
  );
};
